import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Plus, Calendar, MapPin, Clock, Users, Edit, Trash2, ListChecks,
  CheckCircle2, HelpCircle, X, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';

const courseOptions = ['Football', 'Cricket', 'Badminton', 'Swimming', 'Tennis', 'Basketball', 'Hockey', 'Athletics'];
const batchOptions = ['Morning', 'Afternoon', 'Evening', 'Weekend'];
const locationSuggestions = ['Main Ground', 'Court 1', 'Court 2', 'Indoor Hall', 'Pool', 'Practice Field', 'Stadium'];

const emptyForm = () => ({
  title: '',
  course: '',
  batches: [],          // multi-select
  date: new Date().toISOString().slice(0, 10),
  start_time: '16:00',
  duration_minutes: 60,
  locations: [],        // multi-select
  drills: '',
  drill_ids: [],        // Phase 102 — Drill Library references
  notes: '',
  max_participants: '',
});

// Reusable chip multi-picker — toggle preset options or add free-form values
function MultiPicker({ values, options, onChange, placeholder, allowCustom = true, testIdBase }) {
  const [draft, setDraft] = useState('');
  const set = new Set(values || []);
  const toggle = (v) => {
    const s = (v || '').trim();
    if (!s) return;
    const next = set.has(s) ? values.filter(x => x !== s) : [...values, s];
    onChange(next);
  };
  const remove = (v) => onChange(values.filter(x => x !== v));
  const addDraft = () => {
    const s = draft.trim();
    if (!s) return;
    if (!set.has(s)) onChange([...values, s]);
    setDraft('');
  };

  return (
    <div className="space-y-2">
      {/* Selected chips */}
      {values.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid={`${testIdBase}-chips`}>
          {values.map((v) => (
            <Badge key={v} variant="secondary" className="pl-2 pr-1 py-0.5 gap-1 text-xs">
              {v}
              <button type="button" onClick={() => remove(v)} className="ml-0.5 hover:text-red-600 rounded-full p-0.5" data-testid={`${testIdBase}-chip-remove-${v}`}>
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
      {/* Preset toggle pills */}
      <div className="flex flex-wrap gap-1.5">
        {(options || []).map((opt) => {
          const active = set.has(opt);
          return (
            <button
              key={opt}
              type="button"
              onClick={() => toggle(opt)}
              className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${
                active
                  ? 'bg-teal-600 text-white border-teal-600 shadow-sm'
                  : 'bg-background text-muted-foreground border-border hover:border-teal-400 hover:text-teal-600'
              }`}
              data-testid={`${testIdBase}-option-${opt}`}
            >
              {opt}
            </button>
          );
        })}
      </div>
      {/* Free-form add */}
      {allowCustom && (
        <div className="flex gap-1.5">
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addDraft(); } }}
            placeholder={placeholder || 'Add custom…'}
            className="h-8 text-xs"
            data-testid={`${testIdBase}-custom-input`}
          />
          <Button type="button" size="sm" variant="outline" className="h-8" onClick={addDraft} data-testid={`${testIdBase}-add`}>Add</Button>
        </div>
      )}
    </div>
  );
}

export default function TrainingSessions() {
  const { user } = useAuth();
  const isStaff = ['academy_owner', 'super_admin', 'coach', 'employee'].includes(user?.role);
  const isStudent = user?.role === 'student';

  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [tab, setTab] = useState('upcoming');
  const [detailSession, setDetailSession] = useState(null);
  // Phase 102 — Drill Library integration
  const [libraryDrills, setLibraryDrills] = useState([]);

  const fetchSessions = useCallback(async () => {
    try {
      const res = await api.get('/training-sessions', { params: { upcoming_only: tab === 'upcoming' ? true : undefined } });
      setSessions(res.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  // Phase 102 — Hydrate the drill library once for staff users so the "Add from Library"
  // picker on the session create modal works without a delay when first opened.
  useEffect(() => {
    if (!isStaff) return;
    api.get('/drills').then(r => setLibraryDrills(r.data || [])).catch(() => setLibraryDrills([]));
  }, [isStaff]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        title: form.title,
        course: form.course || null,
        batches: form.batches || [],
        // legacy mirror for older clients/views
        batch: (form.batches || [])[0] || '',
        date: form.date,
        start_time: form.start_time,
        duration_minutes: parseInt(form.duration_minutes) || 60,
        max_participants: form.max_participants ? parseInt(form.max_participants) : null,
        locations: form.locations || [],
        location: (form.locations || [])[0] || '',
        drills: (form.drills || '').split(',').map(s => s.trim()).filter(Boolean),
        drill_ids: form.drill_ids || [],  // Phase 102
        notes: form.notes || '',
      };
      if (editingId) {
        await api.put(`/training-sessions/${editingId}`, payload);
        toast.success('Session updated');
      } else {
        await api.post('/training-sessions', payload);
        const summary = (payload.batches || []).length > 1
          ? `Session created for ${payload.batches.length} batches — students notified`
          : 'Session created — students notified';
        toast.success(summary);
      }
      setShowCreate(false);
      setEditingId(null);
      setForm(emptyForm());
      fetchSessions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  const handleEdit = (s) => {
    setEditingId(s.id);
    const batches = Array.isArray(s.batches) && s.batches.length > 0
      ? s.batches
      : (s.batch ? [s.batch] : []);
    const locations = Array.isArray(s.locations) && s.locations.length > 0
      ? s.locations
      : (s.location ? [s.location] : []);
    setForm({
      title: s.title || '',
      course: s.course || '',
      batches,
      date: s.date || new Date().toISOString().slice(0, 10),
      start_time: s.start_time || '16:00',
      duration_minutes: s.duration_minutes || 60,
      locations,
      drills: (s.drills || []).join(', '),
      drill_ids: s.drill_ids || [],  // Phase 102
      notes: s.notes || '',
      max_participants: s.max_participants || '',
    });
    setShowCreate(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this session?')) return;
    try {
      await api.delete(`/training-sessions/${id}`);
      toast.success('Session deleted');
      fetchSessions();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleRSVP = async (sessionId, status) => {
    try {
      await api.post(`/training-sessions/${sessionId}/rsvp`, { status });
      toast.success(`RSVP: ${status}`);
      fetchSessions();
    } catch (err) {
      toast.error('Failed to RSVP');
    }
  };

  const showDetail = async (s) => {
    try {
      const res = await api.get(`/training-sessions/${s.id}`);
      setDetailSession(res.data);
    } catch {
      setDetailSession(s);
    }
  };

  if (loading) return (
    <div className="space-y-4" data-testid="training-loading">
      <Skeleton className="h-8 w-48" />
      <div className="grid gap-3">{[1,2,3].map(i => <Skeleton key={i} className="h-32 rounded-xl" />)}</div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="training-sessions-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-display font-semibold">Training Sessions</h1>
          <p className="text-sm text-muted-foreground">{sessions.length} session{sessions.length !== 1 ? 's' : ''}</p>
        </div>
        {isStaff && (
          <Dialog open={showCreate} onOpenChange={(v) => { setShowCreate(v); if (!v) { setEditingId(null); setForm(emptyForm()); } }}>
            <DialogTrigger asChild>
              <Button size="sm" data-testid="training-new-button"><Plus className="h-4 w-4 mr-1" /> New Session</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>{editingId ? 'Edit Session' : 'Plan Training Session'}</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
                <div className="space-y-1.5">
                  <Label className="text-xs">Title *</Label>
                  <Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} placeholder="e.g. Sprint & Passing Drills" required data-testid="training-title-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Course</Label>
                    <Select value={form.course} onValueChange={(v) => setForm({...form, course: v})}>
                      <SelectTrigger><SelectValue placeholder="Any" /></SelectTrigger>
                      <SelectContent>{courseOptions.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Batches <span className="text-muted-foreground">({form.batches.length || 'any'})</span></Label>
                    <p className="text-[10px] text-muted-foreground -mt-1">Tap to schedule across multiple batches</p>
                  </div>
                </div>
                <MultiPicker
                  values={form.batches}
                  options={batchOptions}
                  onChange={(v) => setForm({ ...form, batches: v })}
                  placeholder="Custom batch (e.g. U-14 Squad)…"
                  testIdBase="training-batches"
                />
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1.5"><Label className="text-xs">Date *</Label><Input type="date" value={form.date} onChange={(e) => setForm({...form, date: e.target.value})} required /></div>
                  <div className="space-y-1.5"><Label className="text-xs">Time</Label><Input type="time" value={form.start_time} onChange={(e) => setForm({...form, start_time: e.target.value})} /></div>
                  <div className="space-y-1.5"><Label className="text-xs">Duration (min)</Label><Input type="number" value={form.duration_minutes} onChange={(e) => setForm({...form, duration_minutes: e.target.value})} /></div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Locations <span className="text-muted-foreground">({form.locations.length || 'any'})</span></Label>
                  <p className="text-[10px] text-muted-foreground -mt-1">Run the same session across multiple grounds/courts</p>
                  <MultiPicker
                    values={form.locations}
                    options={locationSuggestions}
                    onChange={(v) => setForm({ ...form, locations: v })}
                    placeholder="Custom ground / venue…"
                    testIdBase="training-locations"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Drills from Library <span className="text-muted-foreground">({form.drill_ids.length || 'none'})</span></Label>
                  <p className="text-[10px] text-muted-foreground -mt-1">Tap a drill chip to attach. Manage the library at <a href="/drills" className="underline text-teal-600">Drill Library</a>.</p>
                  {libraryDrills.length === 0 ? (
                    <p className="text-xs text-muted-foreground italic" data-testid="training-drill-lib-empty">No drills in library yet — add some at <a href="/drills" className="underline">Drill Library</a> to attach here.</p>
                  ) : (
                    <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto p-1.5 border rounded-md bg-muted/30" data-testid="training-drill-lib-picker">
                      {libraryDrills.map((d) => {
                        const active = form.drill_ids.includes(d.id);
                        return (
                          <button
                            key={d.id}
                            type="button"
                            onClick={() => {
                              const next = active ? form.drill_ids.filter(x => x !== d.id) : [...form.drill_ids, d.id];
                              setForm({ ...form, drill_ids: next });
                            }}
                            data-testid={`training-drill-lib-toggle-${d.id}`}
                            className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${
                              active
                                ? 'bg-teal-600 text-white border-teal-600 shadow-sm'
                                : 'bg-background text-muted-foreground border-border hover:border-teal-400 hover:text-teal-600'
                            }`}
                          >
                            {d.title}
                            {d.duration_minutes ? <span className="ml-1 opacity-70">· {d.duration_minutes}m</span> : null}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Custom Drills (comma-separated)</Label>
                  <Input value={form.drills} onChange={(e) => setForm({...form, drills: e.target.value})} placeholder="Passing, Sprints, Match Play" data-testid="training-drills-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label className="text-xs">Max Participants</Label><Input type="number" value={form.max_participants} onChange={(e) => setForm({...form, max_participants: e.target.value})} placeholder="20" /></div>
                </div>
                <div className="space-y-1.5"><Label className="text-xs">Notes</Label><Input value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} placeholder="Bring water bottles, wear cleats..." /></div>
                <Button type="submit" className="w-full" data-testid="training-submit">{editingId ? 'Save Changes' : 'Create Session'}</Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="upcoming" data-testid="training-tab-upcoming">Upcoming</TabsTrigger>
          <TabsTrigger value="all" data-testid="training-tab-all">All</TabsTrigger>
        </TabsList>
        <TabsContent value="upcoming" className="mt-4 space-y-3" />
        <TabsContent value="all" className="mt-4 space-y-3" />
      </Tabs>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {sessions.length === 0 ? (
          <Card className="md:col-span-2 lg:col-span-3">
            <CardContent className="p-12 text-center text-muted-foreground">
              <Sparkles className="h-10 w-10 mx-auto mb-3 opacity-40" />
              <p>No sessions yet. {isStaff && 'Create your first training session above.'}</p>
            </CardContent>
          </Card>
        ) : sessions.map((s) => {
          const counts = s.rsvp_counts || {};
          return (
            <Card key={s.id} className="hover:shadow-md transition-shadow border-l-4 border-l-teal-500" data-testid={`training-card-${s.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-2">
                  <CardTitle className="text-base line-clamp-2">{s.title}</CardTitle>
                  {isStaff && (
                    <div className="flex gap-1 shrink-0">
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => handleEdit(s)} data-testid={`training-edit-${s.id}`}>
                        <Edit className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-600" onClick={() => handleDelete(s.id)} data-testid={`training-delete-${s.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap gap-1 mt-1">
                  {s.course && <Badge variant="outline" className="text-[10px]">{s.course}</Badge>}
                  {(Array.isArray(s.batches) && s.batches.length > 0
                    ? s.batches
                    : (s.batch ? [s.batch] : [])
                  ).map((b) => (
                    <Badge key={b} variant="secondary" className="text-[10px]">{b}</Badge>
                  ))}
                </div>
              </CardHeader>
              <CardContent className="space-y-2 cursor-pointer" onClick={() => showDetail(s)}>
                <div className="flex items-center gap-2 text-xs text-muted-foreground"><Calendar className="h-3.5 w-3.5" /> {s.date} · {s.start_time} ({s.duration_minutes} min)</div>
                {((Array.isArray(s.locations) && s.locations.length > 0) || s.location) && (
                  <div className="flex items-start gap-2 text-xs text-muted-foreground">
                    <MapPin className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                    <span className="break-words">
                      {(Array.isArray(s.locations) && s.locations.length > 0 ? s.locations : [s.location]).filter(Boolean).join(' · ')}
                    </span>
                  </div>
                )}
                {(s.drills || []).length > 0 && (
                  <div className="flex items-start gap-2 text-xs">
                    <ListChecks className="h-3.5 w-3.5 text-teal-600 mt-0.5" />
                    <div className="flex flex-wrap gap-1">
                      {(s.drills || []).slice(0, 4).map((d, i) => <Badge key={i} variant="outline" className="text-[10px] bg-teal-50 text-teal-700 dark:bg-teal-900/20">{d}</Badge>)}
                      {(s.drills || []).length > 4 && <span className="text-[10px] text-muted-foreground">+{s.drills.length - 4}</span>}
                    </div>
                  </div>
                )}

                {/* RSVP counts */}
                <div className="flex items-center gap-3 text-xs pt-2 border-t border-border/50">
                  <span className="flex items-center gap-1 text-emerald-600 font-medium"><CheckCircle2 className="h-3.5 w-3.5" /> {counts.going || 0}</span>
                  <span className="flex items-center gap-1 text-amber-600 font-medium"><HelpCircle className="h-3.5 w-3.5" /> {counts.maybe || 0}</span>
                  <span className="flex items-center gap-1 text-red-600 font-medium"><X className="h-3.5 w-3.5" /> {counts.skip || 0}</span>
                  {s.max_participants && <span className="ml-auto flex items-center gap-1 text-muted-foreground"><Users className="h-3 w-3" /> {counts.going || 0}/{s.max_participants}</span>}
                </div>

                {/* Student RSVP buttons */}
                {isStudent && (
                  <div className="grid grid-cols-3 gap-1.5 pt-2" onClick={(e) => e.stopPropagation()}>
                    <Button size="sm" variant={s.my_rsvp === 'going' ? 'default' : 'outline'} className={`h-7 text-xs ${s.my_rsvp === 'going' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`} onClick={() => handleRSVP(s.id, 'going')} data-testid={`rsvp-going-${s.id}`}>
                      <CheckCircle2 className="h-3 w-3 mr-1" /> Going
                    </Button>
                    <Button size="sm" variant={s.my_rsvp === 'maybe' ? 'secondary' : 'outline'} className="h-7 text-xs" onClick={() => handleRSVP(s.id, 'maybe')} data-testid={`rsvp-maybe-${s.id}`}>
                      Maybe
                    </Button>
                    <Button size="sm" variant={s.my_rsvp === 'skip' ? 'destructive' : 'outline'} className="h-7 text-xs" onClick={() => handleRSVP(s.id, 'skip')} data-testid={`rsvp-skip-${s.id}`}>
                      Skip
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Detail Dialog (for coaches viewing RSVP list) */}
      <Dialog open={!!detailSession} onOpenChange={() => setDetailSession(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{detailSession?.title}</DialogTitle>
          </DialogHeader>
          {detailSession && (
            <div className="space-y-3">
              <div className="text-sm text-muted-foreground">
                {detailSession.date} · {detailSession.start_time} · {detailSession.duration_minutes} min
              </div>
              {(Array.isArray(detailSession.batches) && detailSession.batches.length > 0
                ? detailSession.batches
                : (detailSession.batch ? [detailSession.batch] : [])
              ).length > 0 && (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground">Batches</p>
                  <div className="flex flex-wrap gap-1">
                    {(Array.isArray(detailSession.batches) && detailSession.batches.length > 0
                      ? detailSession.batches
                      : [detailSession.batch]
                    ).filter(Boolean).map((b, i) => <Badge key={i} variant="secondary">{b}</Badge>)}
                  </div>
                </div>
              )}
              {((Array.isArray(detailSession.locations) && detailSession.locations.length > 0) || detailSession.location) && (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground">Locations</p>
                  <div className="flex flex-wrap gap-1">
                    {(Array.isArray(detailSession.locations) && detailSession.locations.length > 0
                      ? detailSession.locations
                      : [detailSession.location]
                    ).filter(Boolean).map((l, i) => (
                      <Badge key={i} variant="outline" className="bg-blue-50 text-blue-700 dark:bg-blue-900/20"><MapPin className="h-3 w-3 mr-1" />{l}</Badge>
                    ))}
                  </div>
                </div>
              )}
              {detailSession.notes && <p className="text-sm">{detailSession.notes}</p>}
              {(detailSession.drills || []).length > 0 && (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground">Drills</p>
                  <div className="flex flex-wrap gap-1">
                    {detailSession.drills.map((d, i) => <Badge key={i} variant="outline" className="bg-teal-50 text-teal-700">{d}</Badge>)}
                  </div>
                </div>
              )}
              {isStaff && (detailSession.rsvps || []).length > 0 && (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground">Responses ({detailSession.rsvps.length})</p>
                  <div className="max-h-60 overflow-y-auto space-y-1.5">
                    {detailSession.rsvps.map((r, idx) => (
                      <div key={idx} className="flex items-center justify-between text-sm border-b py-1.5">
                        <span>{r.student_name}</span>
                        <Badge variant="outline" className={
                          r.status === 'going' ? 'bg-emerald-50 text-emerald-700' :
                          r.status === 'maybe' ? 'bg-amber-50 text-amber-700' :
                          'bg-red-50 text-red-700'
                        }>{r.status}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
